import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserPermissions } from '../services/api';
import { useAuth } from './AuthContext';

interface PermissionsState {
    /** Whether the user has the Bindle Lock (full access to all pages) */
    hasFullAccess: boolean;
    /** List of allowed page paths (["*"] for full access, ["/amzl-updates"] for restricted) */
    allowedPages: string[];
    /** Whether permissions are still being fetched from the backend */
    loading: boolean;
    /** Check if a given path is accessible to the current user */
    canAccess: (path: string) => boolean;
    /** Re-fetch permissions (e.g., after granting access) */
    refresh: () => void;
}

const defaultState: PermissionsState = {
    hasFullAccess: false,
    allowedPages: ['/amzl-updates'],
    loading: true,
    canAccess: () => false,
    refresh: () => {},
};

const PermissionsContext = createContext<PermissionsState>(defaultState);

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const [allowedPages, setAllowedPages] = useState<string[]>(['/amzl-updates']);
    const [loading, setLoading] = useState(true);

    const fetchPermissions = useCallback(async () => {
        if (!isAuthenticated) {
            setHasFullAccess(false);
            setAllowedPages(['/amzl-updates']);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const result = await getUserPermissions();
            setHasFullAccess(result.hasFullAccess);
            setAllowedPages(result.allowedPages);
        } catch (err) {
            // Fail safe — restrict access on error
            console.error('Failed to fetch permissions, restricting access:', err);
            setHasFullAccess(false);
            setAllowedPages(['/amzl-updates']);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const canAccess = useCallback(
        (path: string): boolean => {
            if (hasFullAccess || allowedPages.includes('*')) return true;
            // Normalize: "/" and "/amzl-updates" are the same page
            const normalizedPath = path === '/' ? '/amzl-updates' : path;
            return allowedPages.includes(normalizedPath);
        },
        [hasFullAccess, allowedPages],
    );

    return (
        <PermissionsContext.Provider
            value={{
                hasFullAccess,
                allowedPages,
                loading,
                canAccess,
                refresh: fetchPermissions,
            }}
        >
            {children}
        </PermissionsContext.Provider>
    );
};
